import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { liveApi } from '../../config/config';
import { isDemo } from './telemetryStore';

/**
 * "Missing data" — patients on this board whose ECG is not reaching the
 * server (GET /api/live/missing-data, a read-only check). Nothing is fixed
 * from here: each row says what to do with the existing tools. Acknowledge
 * hides a row in THIS browser until the problem changes (new key).
 */
const POLL_MS = 60000;
const ACK_KEY = 'lm_missing_ack';

const loadAcks = () => { try { return JSON.parse(localStorage.getItem(ACK_KEY) || '{}'); } catch (e) { return {}; } };
const saveAcks = a => { try { localStorage.setItem(ACK_KEY, JSON.stringify(a)); } catch (e) { /* storage blocked */ } };

export function useMissingData(deviceIds) {
  const [data, setData] = useState({ problems: [], error: '', checkedAt: 0 });
  const [acks, setAcks] = useState(loadAcks);
  const idsKey = (deviceIds || []).slice().sort().join(',');
  const idsRef = useRef(idsKey);
  idsRef.current = idsKey;

  const load = useCallback(() => {
    if (isDemo()) return;
    if (!idsRef.current) { setData({ problems: [], error: '', checkedAt: Date.now() }); return; }
    axios.get(`${liveApi}api/live/missing-data?devices=${encodeURIComponent(idsRef.current)}`)
      .then(r => setData({ problems: r.data.problems || [], error: '', checkedAt: r.data.checkedAt || Date.now() }))
      .catch(e => setData(d => ({ ...d, error: (e.response && e.response.status) ? `server ${e.response.status}` : 'server not reachable' })));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load, idsKey]);

  const acknowledge = useCallback(keys => {
    setAcks(prev => {
      const next = { ...prev };
      keys.forEach(k => { next[k] = Date.now(); });
      // forget acknowledgements older than 7 days
      Object.keys(next).forEach(k => { if (Date.now() - next[k] > 7 * 864e5) delete next[k]; });
      saveAcks(next);
      return next;
    });
  }, []);

  const open = data.problems.filter(p => !acks[p.key]);
  return { ...data, open, acks, acknowledge, reload: load };
}

const KIND = {
  gap: { label: 'Gap', cls: 'critical' },
  stalled: { label: 'No new ECG', cls: 'critical' },
  phone: { label: 'Waiting on phone', cls: 'warning' },
  stuck: { label: 'Not processed', cls: 'warning' },
};
const hhmm = s => String(s || '').slice(5, 16);   // "10-13 14:51"

export function MissingDataPanel({ data, onShowCard }) {
  const [showAll, setShowAll] = useState(false);
  const list = showAll ? data.problems : data.open;
  return (
    <div className="lm-conn-panel">
      <div className="lm-conn-head">
        <span>
          {data.error
            ? `Missing-data check unavailable (${data.error}).`
            : data.problems.length
              ? `${data.open.length} open · ${data.problems.length - data.open.length} acknowledged · checked ${new Date(data.checkedAt).toLocaleTimeString()}`
              : `No missing ECG for the patients on this board · checked ${data.checkedAt ? new Date(data.checkedAt).toLocaleTimeString() : '…'}`}
        </span>
        {data.problems.length > data.open.length && (
          <button type="button" className="lm-conn-ack" onClick={() => setShowAll(v => !v)}>
            {showAll ? 'Hide acknowledged' : 'Show acknowledged'}
          </button>
        )}
        {data.open.length > 1 && (
          <button type="button" className="lm-conn-ack" onClick={() => data.acknowledge(data.open.map(p => p.key))}>
            Acknowledge all
          </button>
        )}
      </div>
      {list.map(p => {
        const k = KIND[p.kind] || KIND.gap;
        const acked = !!data.acks[p.key];
        const when = p.kind === 'gap' ? `${hhmm(p.from_ist)} → ${hhmm(p.to_ist)}` : `since ${hhmm(p.from_ist)}`;
        return (
          <div key={p.key} className={`lm-conn-row lm-conn-row-${k.cls}${acked ? ' lm-conn-row-acked' : ''}`}>
            <span className={`lm-alert-tag lm-alert-${k.cls}`}>{k.label}</span>
            <button type="button" className="lm-conn-who" onClick={() => onShowCard && onShowCard(p.device_id)} title={p.action}>
              <span className="lm-conn-name">{p.patient_name || p.device_id}</span>
              <span className="lm-conn-meta">{p.device_id} · {p.detail} · {when}</span>
              <span className="lm-conn-meta" style={{ fontStyle: 'italic' }}>What to do: {p.action}</span>
            </button>
            <span className="lm-conn-for">{p.minutes != null ? `${p.minutes} min` : p.count != null ? `${p.count} min` : ''}</span>
            {acked
              ? <span className="lm-conn-acked">acknowledged</span>
              : <button type="button" className="lm-conn-ack" onClick={() => data.acknowledge([p.key])}>Acknowledge</button>}
          </div>
        );
      })}
    </div>
  );
}
